import { useApolloClient, useMutation } from '@apollo/client'
import React from 'react'
import { User } from '../../auth/types'
import { i18n } from '../../index/index'
import { MonthlyTargetsEditor } from '../monthlyTargets/MonthlyTargetsEditor'
import { MonthlyTargetsFormData, MonthlyTargetValue } from '../monthlyTargets/types'
import { piecesFormat } from '../monthlyTargets/valueFormat'
import {
  fetchStationThumbsUpTarget,
  getThumbsUpMonthlyTargetsByBudget,
  upsertThumbsUpTargetMutation,
} from './thumbableServiceQueries'
import { MonthlyThumbsUp, MonthlyThumbsUpTarget, ThumbsUpTarget } from './thumbableServiceTypes'

interface ThumbsUpTargetsEditorProps {
  date?: Date
  stationId: string
  user?: User
  thumbsUpTargetData?: ThumbsUpTarget
  lastYearOutcomeData?: MonthlyThumbsUp[]
}

const toTargetValues = (targets?: MonthlyThumbsUpTarget[] | null): MonthlyTargetValue[] =>
  (targets ?? []).map((t) => ({ month: t.month, year: t.year, value: t.thumbsUpTarget }))

/**
 * Ilarit got monthly targets in 2027. Earlier years (the 2026 transition year) keep the
 * previous behaviour: only a yearly target and shift weights, and the daily target is
 * derived from the yearly target. Remove once no box for an earlier year is shown anymore.
 */
export const ILARI_FIRST_MONTHLY_YEAR = 2027

/** Ilarit (thumbs-up) wrapper around the shared monthly targets editor. Values are pieces (kpl). */
export function ThumbsUpTargetsEditor({
  date,
  stationId,
  user,
  thumbsUpTargetData,
  lastYearOutcomeData,
}: ThumbsUpTargetsEditorProps) {
  const client = useApolloClient()
  const [upsertThumbsUpTarget] = useMutation(upsertThumbsUpTargetMutation, {
    refetchQueries: [fetchStationThumbsUpTarget, 'FetchStationThumbsUpTarget'],
    fetchPolicy: 'network-only',
  })

  if (!user) {
    return null
  }

  const fetchTargetsByBudget = async (year: number, yearlyTarget: number): Promise<MonthlyTargetValue[]> => {
    const { data } = await client.query<
      { thumbsUpMonthlyTargetsByBudget: MonthlyThumbsUpTarget[] },
      { stationId: string; year: number; yearlyTarget: number }
    >({
      query: getThumbsUpMonthlyTargetsByBudget,
      variables: { stationId, year, yearlyTarget },
      fetchPolicy: 'network-only',
    })
    return toTargetValues(data.thumbsUpMonthlyTargetsByBudget)
  }

  const save = (data: MonthlyTargetsFormData) =>
    upsertThumbsUpTarget({
      variables: {
        stationId,
        data: {
          date,
          thumbsUpTarget: data.yearlyTarget,
          shiftPercentages: data.shiftPercentages,
          monthlyTargets: data.monthlyTargets.map((mt) => ({
            month: mt.month,
            year: mt.year,
            thumbsUpTarget: mt.value,
          })),
        },
      },
    })

  return (
    <MonthlyTargetsEditor
      date={date}
      yearlyTarget={thumbsUpTargetData?.thumbsUpTarget}
      monthlyTargets={toTargetValues(thumbsUpTargetData?.monthlyTargets)}
      shiftPercentages={thumbsUpTargetData?.shiftPercentages}
      lastYearActuals={lastYearOutcomeData?.map((m) => ({ month: m.month, value: m.totalThumbsUp }))}
      labels={{
        title: i18n.t('admin.thumbsUpTargets.annualTargetTitle'),
        divideByBudget: i18n.t('admin.thumbsUpTargets.divideYearlyTargetByBudget'),
        monthlyTargetsHeading: i18n.t('admin.thumbsUpTargets.monthlyTargetsHeadingText'),
        updateError: i18n.t('admin.thumbsUpTargets.updateError'),
      }}
      valueFormat={piecesFormat}
      firstMonthlyYear={ILARI_FIRST_MONTHLY_YEAR}
      canEditYearlyTarget={user.userType === 'SokAdmin'}
      editRequiresYearlyTarget={!!date && date.getFullYear() > new Date().getFullYear()}
      canUseDivideByBudget={user.userType === 'SokAdmin'}
      fetchTargetsByBudget={fetchTargetsByBudget}
      save={save}
    />
  )
}
